import { supabase } from "@/integrations/supabase/client";
import { edgeInvoke } from "@/lib/edgeInvoke";
import { VideoFrame, extractAllVideoFrames, FrameExtractionConfig } from "@/lib/frame-extraction";
import { isEntitlementsGatingEnabled, isMvpEntitlementsOff } from "@/lib/flags";
import { PayloadChunker, defaultChunker } from "@/lib/analysis/chunker";
import { ChunkedOpenAIClient, chunkedOpenAIClient } from "@/lib/analysis/openai-client";
import { ResultAssembler, defaultAssembler } from "@/lib/analysis/assembler";
import { AnalysisError, classifyAnalysisError } from "@/lib/analysis/error-types";

export interface AnalysisRequest {
  video_frames: VideoFrame[];
  playback_id: string;
  video_id?: string;
  classificationKey?: string;
}

// S-2210: Enhanced analysis request with full frame capture
export interface EnhancedAnalysisRequest {
  playback_id: string;
  video_id?: string;
  extractionConfig?: FrameExtractionConfig;
  telemetry?: any;
  duration?: number;
}

export interface AnalysisResult {
  analysisId: string;
  bullets: string[];
  recommendedDrill: {
    id: string;
    title: string;
  } | null;
  scores: {
    overall: number;
    tempo: number;
    form: number;
    power: number;
  };
}

// S-2210: Enhanced analysis with full frame capture and chunking
// S-2211: Added structured error handling and validation
export async function requestEnhancedVideoAnalysis(request: EnhancedAnalysisRequest): Promise<AnalysisResult> {
  try {
    console.log(`[S-2210] Starting enhanced analysis for playback_id: ${request.playback_id}`);
    
    // Step 1: Extract ALL frames using new extraction pipeline
    const extractionConfig: FrameExtractionConfig = {
      strategy: 'all',
      compressionQuality: 0.7,
      maxWidth: 1280,
      maxHeight: 720,
      ...request.extractionConfig
    };
    
    const extractionResult = await extractAllVideoFrames(
      request.playback_id,
      extractionConfig,
      request.duration
    );
    
    console.log(`[S-2210] Frame extraction complete: ${extractionResult.framesCaptured}/${extractionResult.framesDecoded} frames`);
    
    // S-2211: Validate frame count
    const minFramesRequired = 10;
    if (extractionResult.framesCaptured < minFramesRequired) {
      throw AnalysisError.lowFrames(extractionResult.framesCaptured, minFramesRequired);
    }
    
    // S-2211: Validate telemetry if expected
    const hasTelemetry = request.telemetry && 
      (request.telemetry.pose?.landmarks || request.telemetry.hands?.landmarks);
    
    if (!hasTelemetry && request.extractionConfig?.strategy !== 'keyframe') {
      // Only require telemetry for detailed analysis strategies
      console.warn('[S-2211] No telemetry data available for enhanced analysis');
    }
    
    // Step 2: Chunk frames and telemetry for transmission
    const chunks = await defaultChunker.chunkFrames(
      extractionResult.frames,
      request.telemetry
    );
    
    console.log(`[S-2210] Payload chunking complete: ${chunks.length} chunks`);
    
    // Step 3: Submit all chunks to OpenAI
    const chunkedResult = await chunkedOpenAIClient.analyzeChunks(
      chunks,
      request.playback_id
    );
    
    console.log(`[S-2210] Chunked analysis complete: ${chunkedResult.completedParts}/${chunkedResult.totalParts} parts`);
    
    // S-2211: Check for analysis failures
    if (chunkedResult.completedParts === 0) {
      throw AnalysisError.modelError('No analysis parts completed successfully');
    }
    
    // Step 4: Assemble final result
    const { analysis, report } = defaultAssembler.assembleResults(chunkedResult);
    
    console.log(`[S-2210] Assembly complete: quality=${report.qualityScore}%, coverage=${(report.coverageRatio * 100).toFixed(1)}%`);
    
    // S-2211: Validate analysis quality
    if (report.qualityScore < 50) {
      throw AnalysisError.modelError(`Low analysis quality: ${report.qualityScore}% - insufficient visual data`);
    }
    
    // Step 5: Save to database if video_id provided
    if (request.video_id && analysis) {
      await saveAnalysisToDatabase(request.video_id, analysis, {
        extractionResult,
        chunkedResult,
        assemblyReport: report
      });
    }
    
    // Add frame audit to result
    const enhancedResult: AnalysisResult = {
      ...analysis,
      analysisId: chunkedResult.runId,
      recommendedDrill: null // TODO: Implement drill recommendation logic
    };
    
    return enhancedResult;
    
  } catch (error) {
    // S-2211: Convert any error to structured AnalysisError
    if (error instanceof AnalysisError) {
      throw error;
    }
    
    // Classify and wrap unknown errors
    throw classifyAnalysisError(error);
  }
}

// Legacy function for backward compatibility
export async function requestVideoAnalysis(request: AnalysisRequest): Promise<AnalysisResult> {
  const result = await edgeInvoke('pose-analyze-video-only', {
    video_frames: request.video_frames,
    playback_id: request.playback_id
  });

  // If we have video ID in the request, save analysis to database
  if (request.video_id && result) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        await supabase
          .from('analyses')
          .insert({
            video_id: request.video_id,
            user_id: userData.user.id,
            overall_score: result.scores.overall,
            tempo_score: result.scores.tempo,
            form_score: result.scores.form,
            power_score: result.scores.power,
            feedback: result.bullets.join('\n\n'),
            recommendations: null
          });
      }
    } catch (saveError) {
      console.error('Failed to save analysis to database:', saveError);
      // Don't throw here - analysis was successful, just saving failed
    }
  }

  return result;
}

async function saveAnalysisToDatabase(
  videoId: string, 
  analysis: any, 
  metadata: any
): Promise<void> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return;
    
    await supabase
      .from('analyses')
      .insert({
        video_id: videoId,
        user_id: userData.user.id,
        overall_score: analysis.scores.overall,
        tempo_score: analysis.scores.tempo,
        form_score: analysis.scores.form,
        power_score: analysis.scores.power,
        feedback: analysis.bullets.join('\n\n'),
        recommendations: null,
        // S-2210: Store enhanced metadata
        metadata: {
          frameAudit: analysis.frameAudit,
          assemblyReport: metadata.assemblyReport,
          extractionStats: {
            framesDecoded: metadata.extractionResult.framesDecoded,
            framesCaptured: metadata.extractionResult.framesCaptured,
            compressionApplied: metadata.extractionResult.compressionApplied
          }
        }
      });
      
    console.log(`[S-2210] Analysis saved to database for video: ${videoId}`);
  } catch (saveError) {
    console.error('[S-2210] Failed to save enhanced analysis to database:', saveError);
    // Don't throw - analysis was successful, just saving failed
  }
}

export async function getAnalysisForVideo(videoId: string) {
  const { data, error } = await supabase
    .from('analyses')
    .select('*')
    .eq('video_id', videoId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

export async function getUserAnalysisQuota() {
  // S-2220: Bypass all gating in MVP mode (default)
  if (isMvpEntitlementsOff()) {
    return {
      used: 0,
      limit: 999999, // Unlimited in MVP
      remaining: 999999
    };
  }
  
  // S-2201: Bypass entitlements gating in MVP mode (legacy)
  if (!isEntitlementsGatingEnabled()) {
    return {
      used: 0,
      limit: 999999, // Unlimited in MVP
      remaining: 999999
    };
  }

  try {
    const { data, error } = await supabase
      .from('current_usage')
      .select('monthly_analyses_used')
      .single();

    if (error) {
      console.warn('Failed to load usage data:', error);
      // Graceful fallback - allow analysis to proceed
      return {
        used: 0,
        limit: 999999,
        remaining: 999999
      };
    }

    // Get user's plan limit - with graceful error handling
    let limit = 5; // Default free plan limit
    try {
      const { data: entitlement, error: entitlementError } = await supabase
        .from('entitlements')
        .select('monthly_analyses_limit')
        .eq('plan_name', 'free')
        .single();

      if (entitlementError) {
        console.warn('Failed to load entitlements (406 error expected):', entitlementError);
        // Use default limit, don't throw
      } else {
        limit = entitlement?.monthly_analyses_limit || 5;
      }
    } catch (entitlementError) {
      console.warn('Entitlements query failed, using default limit:', entitlementError);
      // Continue with default limit
    }

    const used = data?.monthly_analyses_used || 0;

    return {
      used,
      limit,
      remaining: Math.max(0, limit - used)
    };
  } catch (error) {
    console.warn('Analysis quota check failed, allowing unlimited access:', error);
    // S-2201: Graceful fallback - never block analysis due to quota errors
    return {
      used: 0,
      limit: 999999,
      remaining: 999999
    };
  }
}

// S-2220: Helper function for analysis permission check
export function isAnalysisAllowed(): boolean {
  // S-2220: In MVP mode (default), always allow analysis
  if (isMvpEntitlementsOff()) {
    return true;
  }
  
  // S-2201: Legacy MVP mode check
  return !isEntitlementsGatingEnabled();
}